<script setup lang="ts">
    import AppFormTitle from '@/components/forms/AppFormTitle.vue';
    import { ROUTES } from '@/router';
    import AppSidebar from '@/views/AppSidebar.vue';
    import AppLink from '@/components/AppLink.vue';
    import AppMainView from '@/components/AppMainView.vue';
    import AppHeading from '@/components/AppHeading.vue';
</script>

<template>
    <v-layout>
        <app-sidebar>
            <router-link :to="{ name: ROUTES.HOME }">
                <app-link icon="mdi-arrow-left">Back to your Cases</app-link>
            </router-link>
        </app-sidebar>

        <app-main-view>
            <app-form-title> Formus Labs Terms of Use </app-form-title>
            <app-main-card>
                <app-heading class="app-terms-heading">1. Application of terms</app-heading>
                <ul>
                    <li>
                        1.1 These Terms apply to your use of the Service (as that term is defined
                        below). By clicking <i>I agree</i> or accessing and using the Service:
                        <ol type="a">
                            <li>you agree to these Terms; and</li>
                            <li>
                                where your access and use is on behalf of another person (e.g. a
                                company), you confirm that you are authorised to, and do in fact,
                                agree to these Terms on that person’s behalf and that, by agreeing
                                to these Terms on that person’s behalf, that person is bound by
                                these Terms.
                            </li>
                        </ol>
                    </li>
                    <li>
                        1.2 If you do not agree to these Terms, you are not authorised to access and
                        use the Service, and you must immediately stop doing so.
                    </li>
                </ul>

                <app-heading class="app-terms-heading">2. Changes</app-heading>
                <div>
                    <ul>
                        <li>
                            2.1 We may change these Terms at any time by notifying you of the change
                            by email or by posting a notice on the Website. Unless stated otherwise,
                            any change takes effect from the date set out in the notice. You are
                            responsible for ensuring you are familiar with the latest Terms. By
                            continuing to access and use the Service from the date on which the
                            Terms are changed, you agree to be bound by the changed Terms.
                        </li>
                        <li>2.2 <b> These Terms were last updated on 15 February 2023. </b></li>
                    </ul>
                </div>

                <app-heading class="app-terms-heading">3. Interpretation</app-heading>
                <div>
                    In these Terms:
                    <ul class="app-terms-bulletpoint-list">
                        <li>
                            <i>Confidential Information</i> means any information that is not public
                            knowledge and that is obtained from the other party in the course of, or
                            in connection with, the provision and use of the Service. Our
                            Confidential Information includes Intellectual Property owned by us (or
                            our licensors), including the Formus Software. Your Confidential
                            Information includes the Data.
                        </li>
                        <li>
                            <i>Data</i> means all data, content, and information (including personal
                            information) owned, held, used or created by you or on your behalf that
                            is stored using, or inputted into, the Service.
                        </li>
                        <li>
                            <i>Force Majeure</i> means an event that is beyond the reasonable
                            control of a party, excluding:
                            <ul>
                                <li>
                                    an event to the extent that it could have been avoided by a
                                    party taking reasonable steps or reasonable care; or
                                </li>
                                <li>a lack of funds for any reason.</li>
                            </ul>
                        </li>
                        <li>
                            <i>Formus Partner</i> means our authorised licensees, resellers,
                            distributors and similar.
                        </li>
                        <li>
                            <i>Formus Software</i> means the software owned by us (and our
                            licensors) that is used to provide the Service.
                        </li>
                        <li><i>including</i> and similar words do not imply any limit.</li>
                        <li>
                            <i>Intellectual Property Rights</i> includes copyright and all rights
                            existing anywhere in the world conferred under statute, common law or
                            equity relating to inventions (including patents), registered and
                            unregistered trademarks and designs, circuit layouts, data and
                            databases, confidential information, know-how, and all other rights
                            resulting from intellectual activity
                        </li>
                        <li>
                            <i>Intellectual Property</i> has a consistent meaning, and includes any
                            enhancement, modification or derivative work of the Intellectual
                            Property.
                        </li>
                        <li>
                            <i>Objectionable</i> includes being objectionable, defamatory, obscene,
                            harassing, threatening, harmful, or unlawful in any way.
                        </li>
                        <li>a <i>party</i> includes that party’s permitted assigns.</li>
                        <li>
                            <i>Permitted Users</i> means your personnel who are authorised to access
                            and use the Service on your behalf in accordance with clause 5.3.
                        </li>
                        <li>
                            a <i>person</i> includes an individual, a body corporate, an association
                            of persons (whether corporate or not), a trust, a government department,
                            or any other entity.
                        </li>
                        <li>
                            <i>personal information</i> means information about an identifiable,
                            living person. personnel include officers, employees, contractors and
                            agents, but a reference to your personnel does not include us.
                        </li>
                        <li>
                            <i>Sales Tax</i> means sales tax, value added tax, goods and services
                            tax, or equivalent tax payable under any applicable law.
                        </li>
                        <li>
                            <i>Service</i> means the service having the core functionality described
                            on the Website, as the Website is updated from time to time.
                        </li>
                        <li>
                            <i>Start Date</i> means the date that you first access or use the
                            Service.
                        </li>
                        <li><i>Terms</i> means these terms titled SaaS terms of use.</li>
                        <li>
                            <i>Underlying Systems</i> means the Formus Software, IT solutions,
                            systems and networks (including software and hardware) used to provide
                            the Service, including any third-party solutions, systems and networks.
                        </li>
                        <li>
                            <i>We, us or our</i> means Formus Labs Limited, a New Zealand company,
                            company number 6096077.
                        </li>
                        <li>
                            <i>Website</i> means the internet site at
                            <a href="https://www.formuslabs.com/" target="_blank">
                                https://www.formuslabs.com/ </a
                            >, or such other site notified to you by us.
                        </li>
                        <li>
                            <i>Year</i> means a 12-month period starting on the Start Date or the
                            anniversary of that date.
                        </li>
                        <li>
                            <i>You or your</i> means you or, if clause 1b applies, both you and the
                            other person on whose behalf you are acting. Words in the singular
                            include the plural and vice versa. A reference to a statute includes
                            references to regulations, orders or notices made under or in connection
                            with the statute or regulations and all amendments, replacements or
                            other changes to any of them.
                        </li>
                    </ul>
                </div>

                <app-heading class="app-terms-heading">4. Provision of the service</app-heading>
                <div>
                    <ul>
                        <li>
                            4.1 We must use reasonable efforts to provide the Service:
                            <ol type="a">
                                <li>in accordance with these Terms and applicable law;</li>
                                <li>exercising reasonable care, skill and diligence; and</li>
                                <li>
                                    using suitably skilled, experienced and qualified personnel.
                                </li>
                            </ol>
                        </li>

                        <li>
                            4.2 Our provision of the Service to you is non-exclusive. Nothing in
                            these Terms prevents us from providing the Service to any other person.
                        </li>
                        <li>
                            4.3 Subject to clause 4.4, we must use reasonable efforts to ensure the
                            Service is available on a 24/7 basis. However, it is possible that on
                            occasion the Service may be unavailable to permit maintenance or other
                            development activity to take place, or in the event of Force Majeure. We
                            must use reasonable efforts to publish on the Website and notify you by
                            email advance details of any unavailability.
                        </li>
                        <li>
                            4.4 Through the use of web services and APIs, the Service interoperates
                            with a range of third-party service features. We do not make any
                            warranty or representation on the availability of those features.
                            Without limiting the previous sentence, if a third-party feature
                            provider ceases to provide that feature or ceases to make that feature
                            available on reasonable terms, we may cease to make available that
                            feature to you. To avoid doubt, if we exercise our right to cease the
                            availability of a third-party feature, you are not entitled to any
                            refund, discount or other compensation.
                        </li>
                    </ul>
                </div>

                <app-heading class="app-terms-heading">5. Your obligations</app-heading>
                <div>
                    <ul>
                        <li>
                            5.1 You and your personnel must:
                            <ol type="a">
                                <li>
                                    use the Service in accordance with these Terms solely for:
                                    <ol type="i">
                                        <li>your own internal business purposes; and</li>
                                        <li>lawful purposes; and</li>
                                    </ol>
                                </li>
                                <li>
                                    not resell or make available the Service to any third party, or
                                    otherwise commercially exploit the Service.
                                </li>
                            </ol>
                        </li>
                        <li>5.2 When accessing the Service, you and your personnel must:</li>
                        <li>
                            <ol type="a">
                                <li>
                                    not impersonate another person or misrepresent authorisation to
                                    act on behalf of others or us;
                                </li>
                                <li>
                                    correctly identify the sender of all electronic transmissions;
                                </li>
                                <li>
                                    not attempt to undermine the security or integrity of the
                                    Underlying Systems;
                                </li>
                                <li>
                                    not use, or misuse, the Service in any way which may impair the
                                    functionality of the Underlying Systems or impair the ability of
                                    any other user to use the Service;
                                </li>
                                <li>
                                    not attempt to view, access or copy any material or data other
                                    than:
                                    <ol type="i">
                                        <li>
                                            <a href="https://www.formuslabs.com/" target="_blank">
                                                https://www.formuslabs.com/
                                            </a>
                                            that which you are authorised to access; and
                                        </li>
                                        <li>
                                            to the extent necessary for you to use the Service in
                                            accordance with these Terms; and
                                        </li>
                                    </ol>
                                </li>
                                <li>
                                    neither use the Service in a manner, nor transmit, input or
                                    store any Data, that breaches any third party right (including
                                    Intellectual Property Rights and privacy rights) or is
                                    Objectionable, incorrect or misleading.
                                </li>
                            </ol>
                        </li>
                        <li>
                            5.3 You may authorise any member of your personnel to be a Permitted
                            User and may, at any time, restrict Permitted Users’ access to certain
                            features.
                        </li>
                        <li>
                            5.4 You are responsible for determining the level of access granted to a
                            Permitted User, for amending access rights as necessary from time to
                            time, and for the deletion of any Permitted Users’ account that you no
                            longer wish to have access (e.g. where an employee ceases working for
                            you). We take no responsibility for the level of access granted to a
                            Permitted User, or for the deletion of Permitted Users’ accounts.
                        </li>
                        <li>
                            5.5 You must procure each Permitted User’s compliance with clauses 5.1
                            and 5.2 and any other reasonable condition notified by us to you.
                        </li>
                        <li>
                            5.6 A breach of any of these Terms by your personnel (including, to
                            avoid doubt, a Permitted User) is deemed to be a breach of these Terms
                            by you.
                        </li>
                        <li>
                            5.7 You are responsible for procuring all licences, authorisations and
                            consents required for you and your personnel to use the Service,
                            including to use, store and input Data into, and process and distribute
                            Data through, the Service.
                        </li>
                    </ul>
                </div>

                <app-heading class="app-terms-heading">6. Data</app-heading>
                <div>
                    <ul>
                        <li>
                            6.1 You acknowledge that:
                            <ol type="a">
                                <li>
                                    we may require access to the Data to exercise our rights and
                                    perform our obligations under these Terms; and
                                </li>
                                <li>
                                    to the extent that this is necessary but subject to clause 9, we
                                    may authorise a member or members of our personnel to access the
                                    Data for this purpose.
                                </li>
                            </ol>
                        </li>
                        <li>
                            6.2 You must arrange all consents and approvals that are necessary for
                            us to access the Data as described in clause 6.1.
                        </li>
                        <li>
                            6.3 You acknowledge and agree that:
                            <ol type="a">
                                <li>
                                    we may:
                                    <ol type="i">
                                        <li>
                                            use Data and information about your and your end users’
                                            use of the Service to generate anonymised and aggregated
                                            statistical and analytical data (Analytical Data);
                                        </li>
                                        <li>
                                            use Analytical Data for our internal research and
                                            product development purposes and to conduct statistical
                                            analysis and identify trends and insights; and
                                        </li>
                                        <li>supply Analytical Data to third parties;</li>
                                    </ol>
                                </li>
                                <li>
                                    our rights under clauses 6aii and iii above will survive
                                    termination or expiry of these Terms; and
                                </li>
                                <li>
                                    title to, and all Intellectual Property Rights in, Analytical
                                    Data is and remains our property.
                                </li>
                            </ol>
                        </li>
                        <li>
                            6.4 You acknowledge and agree that to the extent Data contains personal
                            information, in collecting, holding and processing that information
                            through the Service, we are acting as your agent for the purposes of the
                            Privacy Act 1993 and any other applicable privacy law. You must obtain
                            all necessary consents from the relevant individual to enable us to
                            collect, use, hold and process that information in accordance with these
                            Terms.
                        </li>
                        <li>
                            6.5 While we will take standard industry measures to back up all Data
                            stored using the Service, you agree to keep a separate back-up copy of
                            all Data uploaded by you onto the Service.
                        </li>
                        <li>
                            6.6 You agree that we may store Data (including any personal
                            information) in secure servers in the United States and Australia and
                            may access that Data (including any personal information) in the United
                            States, Australia and New Zealand from time to time.
                        </li>
                        <li>
                            6.7 You indemnify us against any liability, claim, proceeding, cost,
                            expense (including the actual legal fees charged by our solicitors) and
                            loss of any kind arising from any actual or alleged claim by a third
                            party that any Data infringes the rights of that third party (including
                            Intellectual Property Rights and privacy rights) or that the Data is
                            Objectionable, incorrect or misleading.
                        </li>
                    </ul>
                </div>

                <app-heading class="app-terms-heading">7. Fees</app-heading>
                <div>
                    <ul>
                        <li>
                            7.1 If you have obtained the right to access and use the Service through
                            a Formus Partner, you must pay the Formus Partner the fees (if any)
                            agreed between you and the Formus Partner for your access and use of the
                            Service.
                        </li>
                        <li>
                            7.2 If you have obtained the right to access and use the Service
                            directly from us:
                            <ol type="a">
                                <li>
                                    You must pay us the applicable fees set out in our proposal or
                                    quote or as agreed otherwise in writing between you and us, as
                                    may be updated from time to time in accordance with clause 7.2f
                                    (Fees).
                                </li>
                                <li>
                                    We will provide you with invoices on a monthly basis prior to
                                    the due date for payment.
                                </li>
                                <li>
                                    The Fees exclude Sales Tax, which you must pay on taxable
                                    supplies.
                                </li>
                                <li>
                                    You must pay the Fees:
                                    <ol type="i">
                                        <li>
                                            in accordance with the payment terms set out in our
                                            proposal or quote or as agreed otherwise in writing
                                            between you and us; and
                                        </li>
                                        <li>
                                            electronically in cleared funds without any set off or
                                            deduction.
                                        </li>
                                    </ol>
                                </li>
                                <li>
                                    We may charge interest on overdue amounts. Interest will be
                                    calculated from the due date to the date of payment (both
                                    inclusive) at an annual percentage rate equal to the corporate
                                    overdraft reference rate (monthly charging cycle) applied by our
                                    primary trading bank as at the due date (or, if our primary
                                    trading bank ceases to quote that rate, then the rate which in
                                    the opinion of the bank is equivalent to that rate in respect of
                                    similar overdraft accommodation expressed as a percentage) plus
                                    2% per annum.
                                </li>
                                <li>
                                    We may increase the Fees by giving at least 30 days’ notice. If
                                    you do not wish to pay the increased Fees, you may terminate
                                    these Terms and your right to access and use the Service on no
                                    less than 10 days’ notice, provided the notice is received by us
                                    before the effective date of the Fee increase. If you do not
                                    terminate these Terms and your right to access and use the
                                    Service in accordance with this clause, you are deemed to have
                                    accepted the increased Fees.
                                </li>
                            </ol>
                        </li>
                    </ul>
                </div>

                <app-heading class="app-terms-heading">8. Intellectual property</app-heading>
                <div>
                    <ul>
                        <li>
                            8.1 Subject to clause 8.2, title to, and all Intellectual Property
                            Rights in, the Service, the Website, and all Underlying Systems is and
                            remains our property (and our licensors’ property). You must not contest
                            or dispute that ownership, or the validity of those Intellectual
                            Property Rights.
                        </li>
                        <li>
                            8.2 Title to, and all Intellectual Property Rights in, the Data (as
                            between the parties) remains your property. You grant us a worldwide,
                            non-exclusive, fully paid up, transferable, irrevocable licence to use,
                            store, copy, modify, make available and communicate the Data for any
                            purpose in connection with the exercise of our rights and performance of
                            our obligations in accordance with these Terms.
                        </li>
                        <li>
                            8.3 To the extent not owned by us, you grant us a royalty-free,
                            transferable, irrevocable and perpetual licence to use for our own
                            business purposes any know-how, techniques, ideas, methodologies, and
                            similar Intellectual Property used by us in the provision of the
                            Service.
                        </li>
                        <li>
                            8.4 If you provide us with ideas, comments or suggestions relating to
                            the Service or Underlying Systems (together feedback):
                            <ol type="a">
                                <li>
                                    all Intellectual Property Rights in that feedback, and anything
                                    created as a result of that feedback (including new material,
                                    enhancements, modifications or derivative works), are owned
                                    solely by us; and
                                </li>
                                <li>we may use or disclose the feedback for any purpose.</li>
                            </ol>
                        </li>
                        <li>
                            8.5 You acknowledge that the Service may link to third party websites or
                            feeds that are connected or relevant to the Service. Any link from the
                            Service does not imply that we endorse, approve or recommend, or have
                            responsibility for, those websites or feeds or their content or
                            operators. To the maximum extent permitted by law, we exclude all
                            responsibility or liability for those websites or feeds.
                        </li>
                    </ul>
                </div>

                <app-heading class="app-terms-heading">9. Confidentiality</app-heading>
                <div>
                    <ul>
                        <li>
                            9.1 Each party must, unless it has the prior written consent of the
                            other party:
                            <ol type="a">
                                <li>
                                    keep confidential at all times the Confidential Information of
                                    the other party;
                                </li>
                                <li>
                                    effect and maintain adequate security measures to safeguard the
                                    other party’s Confidential Information from unauthorised access
                                    or use; and
                                </li>
                                <li>
                                    disclose the other party’s Confidential Information to its
                                    personnel or professional advisors on a need to know basis only
                                    and, in that case, ensure that any personnel or professional
                                    advisor to whom it discloses the other party’s Confidential
                                    Information is aware of, and complies with, clauses 9a and 9b.
                                </li>
                            </ol>
                        </li>
                        <li>
                            9.2 The obligation of confidentiality in clause 9.1 does not apply to
                            any disclosure or use of Confidential Information:
                            <ol type="a">
                                <li>
                                    for the purpose of performing a party’s obligations, or
                                    exercising a party’s rights, under these Terms;
                                </li>
                                <li>
                                    required by law (including under the rules of any stock
                                    exchange);
                                </li>
                                <li>
                                    which is publicly available through no fault of the recipient of
                                    the Confidential Information or its personnel;
                                </li>
                                <li>
                                    which was rightfully received by a party from a third party
                                    without restriction and without breach of any obligation of
                                    confidentiality; or
                                </li>
                                <li>
                                    by us if required as part of a bona fide sale of our business
                                    (assets or shares, whether in whole or in part) to a third
                                    party, provided that we enter into a confidentiality agreement
                                    with the third party on terms no less restrictive than this
                                    clause 9.
                                </li>
                            </ol>
                        </li>
                    </ul>
                </div>

                <app-heading class="app-terms-heading">10. Warranties</app-heading>
                <div>
                    <ul>
                        <li>
                            10.1 Each party warrants that it has full power and authority to enter
                            into, and perform its obligations under, these Terms.
                        </li>
                        <li>
                            10.2 To the maximum extent permitted by law:
                            <ol type="a">
                                <li>
                                    our warranties are limited to those set out in these Terms, and
                                    all other conditions, guarantees or warranties whether expressed
                                    or implied by statute or otherwise (including any warranty of
                                    merchantability of fitness for purpose) are expressly excluded
                                    and, to the extent that they cannot be excluded, liability for
                                    them is limited to NZD1,000.00; and
                                </li>
                                <li>
                                    we make no representation concerning the quality of the Service
                                    and do not promise that the Service will:
                                    <ol type="i">
                                        <li>
                                            meet your requirements or be suitable for a particular
                                            purpose, including that the use of the Service will
                                            fulfil or meet any statutory role or responsibility you
                                            may have; or
                                        </li>
                                        <li>
                                            be secure, free of viruses or other harmful code,
                                            uninterrupted or error free.
                                        </li>
                                    </ol>
                                </li>
                            </ol>
                        </li>
                        <li>
                            10.3 You agree and represent that you are acquiring the Service, and
                            accepting these Terms, for the purpose of trade. The parties agree that:
                            <ol type="a">
                                <li>
                                    to the maximum extent permissible by law, the Consumer
                                    Guarantees Act 1993 and any other applicable consumer protection
                                    legislation does not apply to the supply of the Service or these
                                    Terms; and
                                </li>
                                <li>
                                    it is fair and reasonable that the parties are bound by this
                                    clause 10.3.
                                </li>
                            </ol>
                        </li>
                        <li>
                            10.4 Where legislation or rule of law implies into these Terms a
                            condition or warranty that cannot be excluded or modified by contract,
                            the condition or warranty is deemed to be included in these Terms.
                            However, our liability for any breach of that condition or warranty is
                            limited, at our option, to:
                            <ol type="a">
                                <li>supplying the Service again; and/or</li>
                                <li>paying the costs of having the Service supplied again.</li>
                            </ol>
                        </li>
                    </ul>
                </div>

                <app-heading class="app-terms-heading">11. Liability</app-heading>
                <div>
                    <ul>
                        <li>
                            11.1 Our maximum aggregate liability under or in connection with these
                            Terms or relating to the Service, whether in contract, tort (including
                            negligence), breach of statutory duty or otherwise, must not in any Year
                            exceed an amount equal to the fees paid by you relating to the Service
                            in the 3 month period prior to the date of the first event giving rise
                            to liability. The cap in this clause 11.1 includes the cap set out in
                            clause 10a.
                        </li>
                        <li>
                            11.2 Neither party is liable to the other under or in connection with
                            these Terms or the Service for any
                            <ol type="a">
                                <li>
                                    loss of profit, revenue, savings, business, use, data (including
                                    Data), and/or goodwill; or
                                </li>
                                <li>
                                    consequential, indirect, incidental or special damage or loss of
                                    any kind.
                                </li>
                            </ol>
                        </li>
                        <li>
                            11.3 Clauses 11.1 and 11.2 do not apply to limit our liability under or
                            in connection with these Terms for:
                            <ol type="a">
                                <li>personal injury or death;</li>
                                <li>fraud or wilful misconduct; or</li>
                                <li>a breach of clause 9.</li>
                            </ol>
                        </li>
                        <li>
                            11.4 Clause 11.2 does not apply to limit your liability:
                            <ol type="a">
                                <li>
                                    to pay the fees (if any) payable by you for use of the Service;
                                </li>
                                <li>under the indemnity in clause 6.7; or</li>
                                <li>for those matters stated in clause 11a to 11c.</li>
                            </ol>
                        </li>
                        <li>
                            11.5 Neither party will be responsible, liable, or held to be in breach
                            of these Terms for any failure to perform its obligations under these
                            Terms or otherwise, to the extent that the failure is caused by the
                            other party failing to comply with its obligations under these Terms, or
                            by the negligence or misconduct of the other party or its personnel.
                        </li>
                        <li>
                            11.6 Each party must take reasonable steps to mitigate any loss or
                            damage, cost or expense it may suffer or incur arising out of anything
                            done or not done by the other party under or in connection with these
                            Terms or the Service.
                        </li>
                    </ul>
                </div>

                <app-heading class="app-terms-heading"
                    >12. Term, termination and suspension</app-heading
                >
                <div>
                    <ul>
                        <li>
                            12.1 Unless terminated under this clause 12, these Terms and your right
                            to access and use the Service:
                            <ol type="a">
                                <li>starts on the Start Date; and</li>
                                <li>
                                    continues until:
                                    <ol type="i">
                                        <li>
                                            if you have obtained the right to access and use the
                                            Service through a Formus Partner, the Formus Partner
                                            notifies us that your right to access and use the
                                            Service has been terminated; or
                                        </li>
                                        <li>
                                            if you have obtained the right to access and use the
                                            Service directly from us, a party gives at least 30
                                            days’ notice that these Terms and your access to and use
                                            of the Service will terminate on the expiry of that
                                            notice.
                                        </li>
                                    </ol>
                                </li>
                            </ol>
                        </li>
                        <li>
                            12.2 Either party may, by notice to the other party, immediately
                            terminate these Terms and your right to access and use the Service if
                            the other party:
                            <ol type="a">
                                <li>
                                    breaches any material provision of these Terms and the breach is
                                    not:
                                    <ol type="i">
                                        <li>
                                            remedied within 10 days of the receipt of a notice from
                                            the first party requiring it to remedy the breach; or
                                        </li>
                                        <li>capable of being remedied; or</li>
                                    </ol>
                                </li>
                                <li>
                                    becomes insolvent, liquidated or bankrupt, has an administrator,
                                    receiver, liquidator, statutory manager, mortgagee’s or
                                    chargee’s agent appointed, becomes subject to any form of
                                    insolvency action or external administration, or ceases to
                                    continue business for any reason.
                                </li>
                            </ol>
                        </li>
                        <li>
                            12.3 You may terminate these Terms and your right to access and use the
                            Service in accordance with clause 7f.
                        </li>
                        <li>
                            12.4 Termination of these Terms does not affect either party’s rights
                            and obligations that accrued before that termination.
                        </li>
                        <li>
                            12.5 On termination of these Terms, you must pay all fees for the
                            provision of the Service prior to that termination.
                        </li>
                        <li>
                            12.6 No compensation is payable by us to you as a result of termination
                            of these Terms for whatever reason, and you will not be entitled to a
                            refund of any fees for the provision of the Service that you have
                            already paid.
                        </li>
                        <li>
                            12.7 Except to the extent that a party has ongoing rights to use
                            Confidential Information, at the other party’s request following
                            termination of these Terms, a party must promptly return to the other
                            party or destroy all Confidential Information of the other party that is
                            in the first party’s possession or control.
                        </li>
                        <li>
                            12.8 Without limiting any other right or remedy available to us, we may
                            restrict or suspend your access to and use of the Service and/or delete,
                            edit or remove the relevant Data if we consider that you or any of your
                            personnel have:
                            <ol type="a">
                                <li>
                                    undermined, or attempted to undermine, the security or integrity
                                    of the Service or any Underlying Systems;
                                </li>
                                <li>
                                    used, or attempted to use, the Service:
                                    <ol type="i">
                                        <li>for improper purposes; or</li>
                                        <li>
                                            in a manner, other than for normal operational purposes,
                                            that materially reduces the operational performance of
                                            the Service;
                                        </li>
                                    </ol>
                                </li>
                                <li>
                                    transmitted, inputted or stored any Data that breaches or may
                                    breach these Terms or any third party right (including
                                    Intellectual Property Rights and privacy rights), or that is or
                                    may be Objectionable, incorrect or misleading; or
                                </li>
                                <li>otherwise materially breached these Terms.</li>
                            </ol>
                        </li>
                    </ul>
                </div>

                <app-heading class="app-terms-heading">13. General</app-heading>
                <div>
                    <ul>
                        <li>
                            13.1 Neither party is liable to the other for any failure to perform its
                            obligations under these Terms to the extent caused by Force Majeure.
                        </li>
                        <li>
                            13.2 No person other than you and us has any right to a benefit under,
                            or to enforce, these Terms.
                        </li>
                        <li>
                            13.3 For us to waive a right under these Terms, that waiver must be in
                            writing and signed by us.
                        </li>
                        <li>
                            13.4 Subject to clause 6.4, we are your independent contractor, and no
                            other relationship (e.g. joint venture, agency, trust or partnership)
                            exists under these Terms.
                        </li>
                        <li>
                            13.5 If we need to contact you, we may do so by email or by posting a
                            notice on the Website. You agree that this satisfies all legal
                            requirements in relation to written communications. You may give notice
                            to us under or in connection with these Terms by emailing [insert email
                            address].
                        </li>
                        <li>
                            13.6 These Terms, and any dispute relating to these Terms or the
                            Service, are governed by and must be interpreted in accordance with the
                            laws of New Zealand. Each party submits to the non-exclusive
                            jurisdiction of the Courts of New Zealand in relation to any dispute
                            connected with these Terms or the Service.
                        </li>
                        <li>
                            13.7 Clauses which, by their nature, are intended to survive termination
                            of these Terms, including clauses 6.7, 8, 9, 11, 12.4 to 12.7 and 13.6,
                            continue in force.
                        </li>
                        <li>
                            13.8 If any part or provision of these Terms is or becomes illegal,
                            unenforceable, or invalid, that part or provision is deemed to be
                            modified to the extent required to remedy the illegality,
                            unenforceability or invalidity. If modification is not possible, the
                            part or provision must be treated for all purposes as severed from these
                            Terms. The remainder of these Terms will be binding on you.
                        </li>
                        <li>
                            13.9 Subject to clauses 2.1 and 7f, any variation to these Terms must be
                            in writing and signed by both parties.
                        </li>
                        <li>
                            13.10 These Terms set out everything agreed by the parties relating to
                            the Service, and supersede and cancel anything discussed, exchanged or
                            agreed prior to the Start Date. The parties have not relied on any
                            representation, warranty or agreement relating to the Service that is
                            not expressly set out in these Terms, and no such representation,
                            warranty or agreement has any effect from the Start Date. Without
                            limiting the previous sentence, the parties agree to contract out of
                            sections 9, 12A and 13 of the New Zealand Fair Trading Act 1986, and
                            that it is fair and reasonable that the parties are bound by this clause
                            13.10.
                        </li>
                        <li>
                            13.11 You may not assign, novate, subcontract or transfer any right or
                            obligation under these Terms without our prior written consent, that
                            consent not to be unreasonably withheld. You remain liable for your
                            obligations under these Terms despite any approved assignment,
                            subcontracting or transfer.
                        </li>
                    </ul>
                </div>
            </app-main-card>
        </app-main-view>
    </v-layout>
</template>

<style scoped lang="scss">
    .app-terms-heading {
        margin-top: 8px;
        margin-bottom: 8px;
    }
    ul {
        list-style: none;
        padding-left: 24px;
    }
    ol {
        padding-left: 24px;
    }
    li {
        margin-top: 10px;
        font-size: 0.875rem;
        font-weight: 400;
        line-height: 1.375rem;
        letter-spacing: 0.0071428571em;
    }
    .app-terms-bulletpoint-list,
    .app-terms-bulletpoint-list ul {
        list-style: inherit;
    }
    a {
        color: rgb(var(--v-theme-primary));
        text-decoration: underline;
    }
</style>
